import type { VisitorAutomationMetadata } from "@redotech/redo-storefront-common/cart-attributes";
import {
  getSessionStorageWithExpiry,
  setSessionStorageWithExpiry,
} from "@redotech/redo-web/utils/session-storage-wrapper";
import * as z from "zod";

const SESSION_STORAGE_KEY = "redo-visitor-observation";
const count = z.number().int().nonnegative();
const stateSchema = z.object({
  sessionId: z.uuid(),
  counts: z.object({
    mousemove: count,
    pointermove: count,
    wheel: count,
    scroll: count,
    click: count,
    keydown: count,
    keyup: count,
    touchstart: count,
    touchend: count,
  }),
  signals: z.object({
    agentUserAgent: z.boolean(),
    webdriver: z.boolean(),
    automationArtifact: z.boolean(),
  }),
  clickTimingAutomation: z.boolean().default(false),
});

export type VisitorCounts = z.infer<typeof stateSchema>["counts"];

interface VisitorMeasurements {
  mouseEventRateHz: number | null;
  clickDurationCount: number;
  clickDurationMeanMs: number | null;
  clickDurationStdMs: number | null;
}

export function readVisitorSignals(
  browser: Pick<Navigator, "userAgent" | "webdriver">,
  globals: object,
) {
  return {
    agentUserAgent:
      /chatgpt|openai|perplexity|claude|anthropic|gemini|grokbot|\bgrok\b|instinct|\bhermes\b|operator|\bcomet\b|HeadlessChrome/i.test(
        browser.userAgent,
      ),
    webdriver: browser.webdriver === true,
    automationArtifact: [
      "__playwright",
      "__pw_manual",
      "__puppeteer",
      "domAutomation",
      "domAutomationController",
    ].some((key) => key in globals),
  };
}

export function classifyVisitor(
  counts: VisitorCounts,
  signals?: ReturnType<typeof readVisitorSignals>,
  measurements?: VisitorMeasurements,
): {
  classification: VisitorAutomationMetadata["classification"];
  reason: string;
} {
  if (signals && Object.values(signals).some(Boolean)) {
    return {
      classification: "suspected_automation",
      reason: "automation_signal",
    };
  }
  // Initial heuristic inspired by https://arxiv.org/abs/2607.26935.
  if (
    counts.click >= 3 &&
    counts.touchstart + counts.touchend === 0 &&
    measurements &&
    measurements.clickDurationCount >= 3 &&
    measurements.clickDurationMeanMs !== null &&
    measurements.clickDurationMeanMs >= 0 &&
    measurements.clickDurationMeanMs < 10 &&
    measurements.clickDurationStdMs !== null &&
    measurements.clickDurationStdMs >= 0 &&
    measurements.clickDurationStdMs <= 2 &&
    measurements.mouseEventRateHz !== null &&
    measurements.mouseEventRateHz >= 0 &&
    measurements.mouseEventRateHz <= 2
  ) {
    return { classification: "suspected_automation", reason: "click_timing" };
  }
  const sparseInput =
    counts.click > 0 &&
    Math.max(counts.mousemove, counts.pointermove) <= counts.click &&
    counts.wheel +
      counts.keydown +
      counts.keyup +
      counts.touchstart +
      counts.touchend ===
      0;
  return {
    classification: sparseInput ? "suspected_automation" : "unknown",
    reason: sparseInput
      ? "sparse_input"
      : counts.click === 0
        ? "zero_click_sessions"
        : "input_observed",
  };
}

let started = false;
let checkoutSnapshot: (() => void) | undefined;
let automationMetadata: VisitorAutomationMetadata | undefined;

export function getVisitorAutomationMetadata() {
  return automationMetadata;
}

export function snapshotVisitorBeforeCheckout() {
  checkoutSnapshot?.();
}

export function observeVisitor(
  report: (data: Record<string, unknown>) => void,
  sessionId?: string,
) {
  if (started) {
    return;
  }
  started = true;
  const stored = (() => {
    try {
      const parsed = stateSchema.safeParse(
        getSessionStorageWithExpiry(SESSION_STORAGE_KEY),
      );
      return parsed.success && parsed.data.sessionId === sessionId
        ? parsed.data
        : undefined;
    } catch {
      return undefined;
    }
  })();
  const counts: VisitorCounts = stored?.counts ?? {
    mousemove: 0,
    pointermove: 0,
    wheel: 0,
    scroll: 0,
    click: 0,
    keydown: 0,
    keyup: 0,
    touchstart: 0,
    touchend: 0,
  };
  const initialMousemoveCount = counts.mousemove;
  const start = performance.now();
  const controller = new AbortController();
  let mouseDownAt: number | undefined;
  let clickDurationCount = 0;
  let clickDurationMeanMs = 0;
  let clickDurationSquaredDeviations = 0;
  const measurementsFor = (elapsedMs: number) => {
    const mouseEventCount = counts.mousemove - initialMousemoveCount;
    return {
      window: "page",
      mouseEventCount,
      mouseEventRateHz:
        elapsedMs > 0 ? (mouseEventCount * 1000) / elapsedMs : null,
      clickDurationCount,
      clickDurationMeanMs: clickDurationCount > 0 ? clickDurationMeanMs : null,
      clickDurationStdMs:
        clickDurationCount > 1
          ? Math.sqrt(clickDurationSquaredDeviations / clickDurationCount)
          : null,
    };
  };
  const currentSignals = readVisitorSignals(window.navigator, window);
  const signals = {
    agentUserAgent:
      currentSignals.agentUserAgent || !!stored?.signals.agentUserAgent,
    webdriver: currentSignals.webdriver || !!stored?.signals.webdriver,
    automationArtifact:
      currentSignals.automationArtifact || !!stored?.signals.automationArtifact,
  };
  const persist = () => {
    if (!sessionId) {
      return;
    }
    try {
      setSessionStorageWithExpiry(
        SESSION_STORAGE_KEY,
        {
          sessionId,
          counts,
          signals,
          clickTimingAutomation:
            stored?.clickTimingAutomation ||
            classification.reason === "click_timing",
        },
        2,
      );
    } catch {
      return;
    }
  };
  let classification = classifyVisitor(counts);
  const updateClassification = () => {
    const elapsedMs = Math.round(performance.now() - start);
    const measurements = measurementsFor(elapsedMs);
    classification = classifyVisitor(counts, signals, measurements);
    if (
      stored?.clickTimingAutomation &&
      classification.reason !== "automation_signal"
    ) {
      classification = {
        classification: "suspected_automation",
        reason: "click_timing",
      };
    }
    automationMetadata = sessionId
      ? {
          sessionId,
          version: 3,
          classification: classification.classification,
          zeroClick: counts.click === 0,
          observedAt: Date.now(),
          elapsedMs,
        }
      : undefined;
    return { elapsedMs, measurements };
  };
  updateClassification();
  document.addEventListener(
    "mousedown",
    (event) => {
      if (event.button === 0) {
        mouseDownAt = performance.now();
      }
    },
    { capture: true, passive: true, signal: controller.signal },
  );
  document.addEventListener(
    "mouseup",
    (event) => {
      if (event.button !== 0 || mouseDownAt === undefined) {
        return;
      }
      const durationMs = performance.now() - mouseDownAt;
      mouseDownAt = undefined;
      clickDurationCount++;
      const delta = durationMs - clickDurationMeanMs;
      clickDurationMeanMs += delta / clickDurationCount;
      clickDurationSquaredDeviations +=
        delta * (durationMs - clickDurationMeanMs);
    },
    { capture: true, passive: true, signal: controller.signal },
  );
  window.addEventListener(
    "blur",
    () => {
      mouseDownAt = undefined;
    },
    { signal: controller.signal },
  );
  for (const type of [
    "mousemove",
    "pointermove",
    "wheel",
    "scroll",
    "click",
    "keydown",
    "keyup",
    "touchstart",
    "touchend",
  ] as const) {
    document.addEventListener(
      type,
      () => {
        counts[type]++;
        updateClassification();
      },
      { capture: true, passive: true, signal: controller.signal },
    );
  }
  const snapshot = (trigger: "checkout" | "pagehide" | "timeout") => {
    const { elapsedMs, measurements } = updateClassification();
    report({
      version: 3,
      signals,
      trigger,
      elapsedMs,
      counts: { ...counts },
      measurements,
      ...classification,
    });
  };
  checkoutSnapshot = () => snapshot("checkout");
  const finish = (trigger: "pagehide" | "timeout") => {
    if (controller.signal.aborted) {
      return;
    }
    controller.abort();
    clearTimeout(timer);
    checkoutSnapshot = undefined;
    snapshot(trigger);
    persist();
    if (classification.reason === "sparse_input") {
      automationMetadata = undefined;
    }
  };
  const timer = setTimeout(() => finish("timeout"), 180_000);
  window.addEventListener("pagehide", () => finish("pagehide"), {
    once: true,
    signal: controller.signal,
  });
}
